import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortal } from '../../context/PortalContext';
import PublicHeader from '../../components/public/PublicHeader';
import { feedbackAPI } from '../../services/api';
import {
  ArrowRightIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  StarIcon,
  ArrowPathIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const PortalDashboard = () => {
  const { portalUser, portalLoading, markNotificationsRead } = usePortal();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // feedback_id being replied to
  const [replyText, setReplyText] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);

  // Logged-out users go to portal login; superusers have nothing here → home
  useEffect(() => {
    if (portalLoading) return;
    if (!portalUser) navigate('/portal/login');
    else if (portalUser.is_superuser) navigate('/');
  }, [portalUser, portalLoading, navigate]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy · h:mm a');
    } catch {
      return '—';
    }
  };

  const fetchNotifications = useCallback(async (silent = false) => {
    if (!silent) setNotificationsLoading(true);
    try {
      const resp = await feedbackAPI.portalNotifications();
      const notifs = resp.data?.notifications || [];
      setNotifications(notifs);
      // Don't call refreshUnreadCount() here — PortalContext already polls
      // portalNotifications() every 60s for the badge. Calling it again
      // here doubled the request rate (2x per minute) for no benefit.
    } catch {
      if (!silent) setNotifications([]);
    } finally {
      if (!silent) setNotificationsLoading(false);
      setNotificationsLoaded(true);
    }
  }, []);

  const handleReplySubmit = async (feedbackId) => {
    if (!replyText.trim()) return;
    setReplySubmitting(true);
    try {
      await feedbackAPI.portalReply(feedbackId, replyText.trim());
      // Update local state to show the user's reply immediately
      setNotifications(prev => prev.map(n =>
        n.feedback_id === feedbackId
          ? { ...n, user_reply: replyText.trim(), user_replied_at: new Date().toISOString() }
          : n
      ));
      setReplyText('');
      setReplyingTo(null);
    } catch {
      // ignore — user can retry
    } finally {
      setReplySubmitting(false);
    }
  };

  // Fetch the full notifications list on mount (the badge count is handled by
  // PortalContext's own 60s poll — polling the full list here just refreshes
  // the timeline while the page is open).
  useEffect(() => {
    if (!portalUser || portalUser.is_superuser) return;
    fetchNotifications();
    const interval = setInterval(() => fetchNotifications(true), 60000);
    return () => clearInterval(interval);
  }, [portalUser, fetchNotifications]);

  // Mark all notifications as read after a short delay once loaded
  useEffect(() => {
    if (notificationsLoaded && notifications.length > 0) {
      const timer = setTimeout(() => {
        markNotificationsRead(notifications.map(n => n.id));
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [notificationsLoaded, notifications, markNotificationsRead]);

  if (portalLoading || !portalUser || portalUser.is_superuser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <svg className="animate-spin h-8 w-8 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PublicHeader activeSection="account" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-12">
        <div className="rounded-2xl border border-gray-200/70 dark:border-gray-700/60 bg-white dark:bg-gray-800 shadow-[0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden">
          <div className="flex items-center gap-2.5 px-5 pt-4 pb-3">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shadow-sm">
              <BellIcon className="w-3.5 h-3.5 text-white" />
            </span>
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-none">Messages</h3>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Replies and updates on your feedback</p>
            </div>
          </div>

          <div className="border-t border-gray-100 dark:border-gray-700/50">
            {notificationsLoading ? (
              <div className="flex items-center justify-center py-16">
                <svg className="animate-spin h-6 w-6 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-700/40 flex items-center justify-center mb-3">
                  <BellIcon className="w-6 h-6 text-gray-300 dark:text-gray-600" />
                </div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">No notifications yet</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-xs">
                  When the Ospito+ team replies to your reviews, feature requests, or messages, you'll see them here.
                </p>
              </div>
            ) : (
              <div className="relative px-5 py-5">
                {/* Timeline line */}
                <div className="absolute left-[34px] top-5 bottom-5 w-px bg-gray-200 dark:bg-gray-700" />

                <div className="space-y-5">
                  {notifications.map((n) => {
                    const iconConfig = {
                      reply_sent: { icon: ChatBubbleLeftRightIcon, bg: 'bg-teal-100 dark:bg-teal-500/20', text: 'text-teal-600 dark:text-teal-400' },
                      review_approved: { icon: StarIcon, bg: 'bg-amber-100 dark:bg-amber-500/20', text: 'text-amber-600 dark:text-amber-400' },
                      status_changed: { icon: ArrowPathIcon, bg: 'bg-indigo-100 dark:bg-indigo-500/20', text: 'text-indigo-600 dark:text-indigo-400' },
                    };
                    const cfg = iconConfig[n.action] || iconConfig.reply_sent;
                    const Icon = cfg.icon;
                    const typeBadge = {
                      review: { label: 'Review', cls: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400' },
                      feature: { label: 'Feature', cls: 'bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-400' },
                      message: { label: 'Message', cls: 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400' },
                    }[n.feedback_type] || { label: n.feedback_type, cls: 'bg-gray-50 text-gray-600 dark:bg-gray-700 dark:text-gray-400' };

                    return (
                      <div key={n.id} className="relative flex gap-4">
                        {/* Timeline dot */}
                        <div className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${cfg.bg} ring-4 ring-white dark:ring-gray-800`}>
                          <Icon className={`w-5 h-5 ${cfg.text}`} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0 pb-1">
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-bold text-gray-900 dark:text-white">{n.title}</p>
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${typeBadge.cls}`}>
                                {typeBadge.label}
                              </span>
                            </div>
                            <span className="text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap flex items-center gap-1 flex-shrink-0">
                              <CalendarIcon className="w-3 h-3" />
                              {formatDateTime(n.timestamp)}
                            </span>
                          </div>

                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Re: <span className="font-medium text-gray-600 dark:text-gray-300">{n.subject}</span>
                          </p>

                          <div className="mt-2 rounded-xl bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700/50 px-3.5 py-2.5">
                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{n.body}</p>
                          </div>

                          <div className="flex items-center gap-1.5 mt-2">
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0">
                              {(n.actor || 'O').charAt(0).toUpperCase()}
                            </div>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500">
                              Replied by <span className="font-medium text-gray-600 dark:text-gray-300">{n.actor}</span>
                            </p>
                          </div>

                          {/* User's reply (if already replied) */}
                          {n.user_reply && (
                            <div className="mt-3 ml-auto max-w-[90%]">
                              <div className="rounded-xl bg-teal-50 dark:bg-teal-500/10 border border-teal-100 dark:border-teal-500/20 px-3.5 py-2.5">
                                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{n.user_reply}</p>
                              </div>
                              <div className="flex items-center justify-end gap-1.5 mt-1.5">
                                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                  You · {formatDateTime(n.user_replied_at)}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Reply input (only for reply_sent items that can be replied to) */}
                          {n.action === 'reply_sent' && n.can_reply && !n.user_reply && (
                            <div className="mt-3">
                              {replyingTo === n.feedback_id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder="Type your reply…"
                                    rows={3}
                                    autoFocus
                                    className="w-full text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/50 px-3 py-2 text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-400 resize-none"
                                  />
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleReplySubmit(n.feedback_id)}
                                      disabled={!replyText.trim() || replySubmitting}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg text-white bg-teal-500 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                      {replySubmitting ? (
                                        <svg className="animate-spin w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                      ) : (
                                        <ArrowRightIcon className="w-3.5 h-3.5" />
                                      )}
                                      Send Reply
                                    </button>
                                    <button
                                      onClick={() => { setReplyingTo(null); setReplyText(''); }}
                                      className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() => { setReplyingTo(n.feedback_id); setReplyText(''); }}
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
                                >
                                  <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />
                                  Reply
                                </button>
                              )}
                            </div>
                          )}

                          {/* Closed indicator */}
                          {n.action === 'reply_sent' && !n.can_reply && !n.user_reply && (
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2 italic">This conversation has been closed.</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalDashboard;
